import { RequestStatus } from 'typed/api/enumerations';
import { SortOrder } from 'utils/constants';
import { WithRouterProps } from 'react-router';

let defaults: { [key: string]: any };
const constructors: any[] = [];
const names: string[] = [];
const FIRST_RESERVED_ID: number = -1000;//reserve some ID for pseudo-entities who don't have DB representation, but might be generated by the server; counterpart to EntityBase.FIRST_RESERVED_ID
let currentUniqID: number = FIRST_RESERVED_ID - 1;

export function uniqID(): number {
    return currentUniqID--;
}

export function watchReportID(type: string, id: number): string {
    return `${type}_${id}`;
}

export function isInternalID(id: number): boolean {
    return id < FIRST_RESERVED_ID; // TODO find a better way to distinguish local temporary IDs and DB ones
}

export function registerEntity(constructor: { constructFromObject: Function }, name: string): void {
    constructors.push(constructor);
    names.push(name);
}

export function getEntityName(constructor: { constructFromObject: Function }): string {
    return names[constructors.indexOf(constructor)];
}

export function unwrapAPIResponse(response: any): any {
    defaults = defaults || require('./defaults').default || require('./defaults');
    if (Array.isArray(response)) {
        response = response.map(unwrapAPIResponse);
    }
    if (response && response.constructor && (response.constructor as any).constructFromObject) {
        const entityName: string = getEntityName(response.constructor);
        if (entityName === 'DatabaseEntry') {
            return response.PrimaryId;
        }

        for (const i in response) {
            if (response.hasOwnProperty(i)) {
                response[i] = response[i] !== undefined ? unwrapAPIResponse(response[i]) : null; // check warnings.js
            }
        }

        response = Object.assign({}, defaults[entityName], response);
        if (response.Custom && response.Custom.status) {
            response.Custom.status = RequestStatus.SUCCEEDED;
        }
        if (response.Custom && response.Custom.internalID) {
            delete response.Custom.internalID;
        }
    }
    return response;
}

export function stripInternalsFromAPIArguments(args: any, stripDefaults: boolean = false): any {
    defaults = defaults || require('./defaults').default || require('./defaults');
    if (Array.isArray(args)) {
        args = args.map(arg => stripInternalsFromAPIArguments(arg, stripDefaults));
    }
    else if (args && (args.constructor && args.constructor.constructFromObject || args.EntityType)) {
        const entityName = args.EntityType || getEntityName(args.constructor);

        args = { ...args };
        for (const i in args) {
            if (args.hasOwnProperty(i)) {
                if (
                    i.indexOf('ID') == i.length - 2 && isInternalID(args[i]) ||
                    i == 'Custom' ||
                    i == 'EntityType' ||
                    stripDefaults && args[i] === defaults[entityName][i]
                ) {
                    delete args[i];
                } else if (args[i] && typeof args[i] === 'object') {
                    args[i] = stripInternalsFromAPIArguments(args[i], stripDefaults);
                }
            }
        }
    }
    return args;
}

function lowerCamelToAllCaps(s: string): string {
    return s.replace(/([a-z])([A-Z]+)/g, '$1_$2').toUpperCase();
}

export function checkAPIEnum(syncEnum: any, APIArray: any, tableName: string): void {
    for (var i = 0; i < APIArray.length; i++) {
        let localEnumID;
        if (DEBUG) {
            try {
                localEnumID = syncEnum[lowerCamelToAllCaps(APIArray[i].ShortIdentifier || APIArray[i].Identifier || '')];
            }
            catch (e) {
            }
        }
        else {
            localEnumID = syncEnum[lowerCamelToAllCaps(APIArray[i].ShortIdentifier || APIArray[i].Identifier || '')];
        }
        if (localEnumID !== APIArray[i].ID) {
            console.error(`UI and ${tableName} are out of sync`);
            return;
        }
    }
    for (var j in syncEnum) {
        if (syncEnum.hasOwnProperty(j) && !APIArray.getByID(syncEnum[j])) {
            console.error(`UI and ${tableName} are out of sync`);
        }
    }
}

export function mergePagination(oldObj: any, newObj: any): any {
    if (newObj.Order === SortOrder.DESCENDING) {
        newObj.Data = newObj.Data.concat(oldObj.Data.filter((data: any) => !newObj.Data.getByID(data.ID)));
    }
    else {
        newObj.Data = oldObj.Data.filter((data: any) => !newObj.Data.getByID(data.ID)).concat(newObj.Data);
    }
    newObj.Count = newObj.Data.length;
    newObj.Custom = oldObj.Custom;
    return newObj;
}

export function entityToQuery(entity: any, queryToExtend: any = {}): any {
    defaults = defaults || require('./defaults').default || require('./defaults');
    const defaultValues = defaults[entity.EntityType] || {};
    const result: { [key: string]: any } = { ...queryToExtend };
    for (const i in entity) {
        if (entity.hasOwnProperty(i) && entity[i] !== defaultValues[i] && i !== 'Custom' && i !== 'EntityType') {
            result[i] = entity[i];
            if (Array.isArray(result[i])) {
                result[i] = (result[i] as string[]).join('_');
            }
            else if (result[i] instanceof Date) {
                result[i] = (result[i] as Date).toISOString();
            }
            else if (typeof result[i] === 'object' && result[i]) {
                result[i] === JSON.stringify(result[i]);
            }
            if (!result[i]) {
                delete result[i];
            }
        }
        else {
            delete result[i];
        }
    }
    return result;
}

const ISO_DATE_RE = /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/;

export function queryToEntity<T>(query: WithRouterProps['location']['query'], entityDefaults: T): T;
export function queryToEntity(query: any, entityConstructor: { constructFromObject: Function }): any;
export function queryToEntity(query: any, defaultsOrConstructor: any | { constructFromObject: Function }): any {
    defaults = defaults || require('./defaults').default || require('./defaults');
    const defaultValues = 'constructFromObject' in defaultsOrConstructor ? defaults[getEntityName(defaultsOrConstructor)] : defaultsOrConstructor;
    let result = Object.assign({}, defaultValues);
    for (const i in query) {
        if (query.hasOwnProperty ? query.hasOwnProperty(i) : query[i]) {
            result[i] = query[i];
            if (Array.isArray(defaultValues[i])) {
                result[i] = result[i].split('_');
            }
            else if (defaults[i] instanceof Date || ISO_DATE_RE.test(result[i])) {
                result[i] = new Date(result[i]);
            }
            else if (typeof defaultValues[i] === 'object') {
                result[i] === JSON.parse(result[i]);
            }
        }
    }
    if ('constructFromObject' in defaultsOrConstructor) {
        result = defaultsOrConstructor.constructFromObject(result);
    }
    result.EntityType = defaultValues.EntityType;
    result.Custom = defaultValues.Custom;

    return result;
}

export function queryToObject(query: any, object: any): any {
    let result = { ...object };

    for (const property in query) {
        if (query.hasOwnProperty ? query.hasOwnProperty(property) : query[property]) {
            result[property] = query[property];
            if (Array.isArray(object[property])) {
                result[property] = result[property].split('_');

                if (result[property].every((item: string) => /^\d+$/.test(item))) {
                    result[property] = result[property].map((item: string) => parseInt(item));
                }
            }
            else if (ISO_DATE_RE.test(result[property])) {
                result[property] = new Date(result[property]);
            }
            else if (/^\d+$/.test(result[property])) {
                result[property] = parseInt(result[property]);
            }
            else if (result[property] === 'true' || result[property] === 'false') {
                result[property] = result[property] === 'true';
            }
            else if (typeof object[property] === 'object') {
                result[property] = JSON.parse(result[property]);
            }
        }
    }

    return result;
}

export function objectToQuery(object: any, queryToExtend: any = {}): any {
    let result: { [key: string]: any } = { ...queryToExtend };
    for (const property in object) {
        if (object.hasOwnProperty(property)) {
            result[property] = object[property];

            if (Array.isArray(result[property])) {
                result[property] = (result[property] as string[]).join('_');
            }
            else if (result[property] instanceof Date) {
                result[property] = (result[property] as Date).toISOString();
            }
            else if ((typeof result[property] === 'object' || typeof result[property] === 'boolean') && result[property]) {
                result[property] = JSON.stringify(result[property]);
            }

            if (!result[property] && result[property] !== false) {
                delete result[property];
            }
        }
    }

    return result;
}